/* Poster rotunda: a small progressive enhancement over genuine product links. */
(() => {
  'use strict';
  if (customElements.get('afterdark-gallery')) return;
  class AfterdarkGallery extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.cards = [...this.querySelectorAll('[data-ad-poster]')];
      this.stage = this.querySelector('[data-ad-stage]');
      if (!this.cards.length || !this.stage) return;
      this.initialized = true;
      this.active = 0;
      this.dataset.adCount = String(this.cards.length);
      this.trackButtons = [...this.querySelectorAll('[data-ad-track]')];
      this.orbit = this.querySelector('[data-ad-orbit]');
      this.reduceMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.handlers = new AbortController();
      const listen = (el, event, callback, options = {}) => el?.addEventListener(event, callback, { ...options, signal: this.handlers.signal });
      this.classList.add('is-ready');
      this.stage.setAttribute('aria-label', 'Utvalda musikposters. Använd piltangenterna för att byta motiv.');
      this.querySelectorAll('[data-ad-controls],[data-ad-tracklist],[data-ad-hint]').forEach(el => { el.hidden = this.cards.length < 2; });
      listen(this.querySelector('[data-ad-prev]'), 'click', () => this.select(this.active - 1, true));
      listen(this.querySelector('[data-ad-next]'), 'click', () => this.select(this.active + 1, true));
      this.trackButtons.forEach(button => listen(button, 'click', () => this.select(Number(button.dataset.adTrack), true)));
      this.cards.forEach((card, index) => listen(card, 'click', event => {
        if (performance.now() < (this.suppressClickUntil || 0)) { event.preventDefault(); return; }
        if (this.active !== index) { event.preventDefault(); this.select(index, true); }
      }));
      listen(this.stage, 'keydown', event => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        if (event.key === 'Home') this.select(0, true);
        else if (event.key === 'End') this.select(this.cards.length - 1, true);
        else this.select(this.active + (event.key === 'ArrowLeft' ? -1 : 1), true);
      });
      listen(this.stage, 'dragstart', event => event.preventDefault());
      listen(this.stage, 'pointerdown', event => {
        if (this.cards.length < 2 || (event.pointerType === 'mouse' && event.button !== 0)) return;
        this.drag = { id: event.pointerId, x: event.clientX, y: event.clientY, lastX: event.clientX, horizontal: false };
      });
      listen(this.stage, 'pointermove', event => {
        if (this.drag && event.pointerId === this.drag.id) {
          const dx = event.clientX - this.drag.x, dy = event.clientY - this.drag.y;
          this.drag.lastX = event.clientX;
          if (Math.abs(dx) > 12 && Math.abs(dx) > Math.abs(dy) * 1.2) {
            this.drag.horizontal = true;
            this.stage.classList.add('is-dragging');
            try { this.stage.setPointerCapture(event.pointerId); } catch (_) { /* Pointer may have ended. */ }
            this.orbit.style.setProperty('--ad-tilt-x', `${Math.max(-5, Math.min(5, dx / 50))}deg`);
          }
          return;
        }
        if (event.pointerType !== 'mouse' || this.reduceMotion.matches || innerWidth < 900) return;
        const box = this.stage.getBoundingClientRect();
        this.orbit.style.setProperty('--ad-tilt-x', `${((event.clientX - box.left) / box.width - .5) * 3.2}deg`);
        this.orbit.style.setProperty('--ad-tilt-y', `${-((event.clientY - box.top) / box.height - .5) * 2}deg`);
      });
      const endDrag = event => {
        if (!this.drag || event.pointerId !== this.drag.id) return;
        const { x, lastX, horizontal } = this.drag;
        this.drag = null;
        this.stage.classList.remove('is-dragging');
        this.orbit.style.setProperty('--ad-tilt-x', '0deg');
        if (horizontal) {
          this.suppressClickUntil = performance.now() + 400;
          if (Math.abs(lastX - x) > 32) this.select(this.active + (lastX < x ? 1 : -1), true);
        }
        try { if (this.stage.hasPointerCapture(event.pointerId)) this.stage.releasePointerCapture(event.pointerId); } catch (_) { /* Ended pointer. */ }
      };
      listen(this.stage, 'pointerup', endDrag);
      listen(this.stage, 'pointercancel', endDrag);
      listen(this.stage, 'pointerleave', () => {
        if (!this.drag) {
          this.orbit.style.setProperty('--ad-tilt-x', '0deg');
          this.orbit.style.setProperty('--ad-tilt-y', '0deg');
        }
      });
      this.resizeObserver = new ResizeObserver(() => this.arrange());
      this.resizeObserver.observe(this.stage);
      this.select(0, false);
    }
    disconnectedCallback() {
      this.handlers?.abort();
      this.resizeObserver?.disconnect();
      this.initialized = false;
    }
    select(index, announce) {
      this.active = (index + this.cards.length) % this.cards.length;
      const card = this.cards[this.active], number = String(this.active + 1).padStart(2, '0');
      this.arrange();
      this.querySelectorAll('[data-ad-number],[data-ad-selected-index]').forEach(el => { el.textContent = number; });
      this.querySelector('[data-ad-product-title]').textContent = card.dataset.title;
      this.querySelector('[data-ad-product-price]').textContent = card.dataset.price;
      this.querySelectorAll('[data-ad-product-link]').forEach(link => { link.href = card.href; });
      this.trackButtons.forEach((button, i) => button.setAttribute('aria-pressed', String(i === this.active)));
      if (announce) {
        if (!this.reduceMotion.matches) this.querySelector('[data-ad-selected]').animate([{ opacity: .4, transform: 'translateY(5px)' }, { opacity: 1, transform: 'translateY(0)' }], { duration: 420, easing: 'cubic-bezier(.2,.7,.2,1)' });
        this.querySelector('[data-ad-status]').textContent = `Poster ${this.active + 1} av ${this.cards.length}: ${card.dataset.title}. ${card.dataset.price}.`;
        const currentTrack = this.trackButtons[this.active];
        if (currentTrack && innerWidth < 900) {
          const track = currentTrack.parentElement;
          const left = currentTrack.offsetLeft - Math.max(0, (track.clientWidth - currentTrack.offsetWidth) / 2);
          track.scrollTo({ left, behavior: this.reduceMotion.matches ? 'instant' : 'smooth' });
        }
      }
    }
    arrange() {
      if (!this.cards?.length) return;
      const mobile = this.stage.clientWidth < 600;
      const tablet = this.stage.clientWidth < 900;
      const width = this.cards[0].offsetWidth;
      const spacing = width * (mobile ? .82 : tablet ? .86 : .98);
      const count = this.cards.length;
      this.cards.forEach((card, i) => {
        let delta = i - this.active;
        if (delta > count / 2) delta -= count;
        if (delta < -count / 2) delta += count;
        const distance = Math.abs(delta), outside = distance > 2;
        card.dataset.adDistance = String(distance);
        card.style.setProperty('--ad-x', `${delta * spacing}px`);
        card.style.setProperty('--ad-y', `${distance * (mobile ? 13 : 20)}px`);
        card.style.setProperty('--ad-z', `${-distance * (mobile ? 190 : 235)}px`);
        card.style.setProperty('--ad-angle', `${-delta * (mobile ? 23 : 26)}deg`);
        card.style.setProperty('--ad-roll', `${delta * (mobile ? 2 : 1.3)}deg`);
        card.style.setProperty('--ad-opacity', outside ? '0' : '1');
        card.style.setProperty('--ad-brightness', distance === 0 ? '1' : distance === 1 ? '.76' : '.5');
        card.style.setProperty('--ad-order', String(10 - distance));
        card.classList.toggle('is-active', i === this.active);
        card.classList.toggle('is-outside', outside);
        card.tabIndex = i === this.active ? 0 : -1;
        card.setAttribute('aria-hidden', String(outside));
        card.setAttribute('aria-label', `${i === this.active ? 'Se' : 'Välj'} ${card.dataset.title}`);
      });
    }
  }
  customElements.define('afterdark-gallery', AfterdarkGallery);
})();
